import { useState, useEffect } from "react"
import { useToast } from "@/components/ui/use-toast"

interface PWAFeatures {
  isInstallable: boolean
  isInstalled: boolean
  isOnline: boolean
  installApp: () => void
  shareContent: (data: ShareData) => Promise<void>
  copyToClipboard: (text: string) => Promise<void>
}

export const usePWAFeatures = (): PWAFeatures => {
  const [isInstallable, setIsInstallable] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null)
  const { toast } = useToast()

  useEffect(() => {
    // Check if app is installed
    const checkInstalled = () => {
      setIsInstalled(
        window.matchMedia('(display-mode: standalone)').matches ||
        window.matchMedia('(display-mode: fullscreen)').matches ||
        // @ts-ignore
        window.navigator.standalone === true
      )
    }

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e)
      setIsInstallable(true)
    }

    // Listen for app installed
    const handleAppInstalled = () => {
      setIsInstalled(true)
      setIsInstallable(false)
      setDeferredPrompt(null)
      toast({
        title: "App installed!",
        description: "Noche Loco is now available on your home screen.",
      })
    }

    // Listen for online/offline status
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    checkInstalled()
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [toast])

  const installApp = async () => {
    if (!deferredPrompt) return

    try {
      deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      
      if (outcome === 'accepted') {
        toast({
          title: "Installing app...",
          description: "Noche Loco will be added to your home screen.",
        })
      }
      
      setDeferredPrompt(null)
      setIsInstallable(false)
    } catch (error) {
      console.error('Install failed:', error)
    }
  }

  const shareContent = async (data: ShareData) => {
    if (navigator.share) {
      try {
        await navigator.share(data)
      } catch (error) {
        // Fallback to clipboard
        if (data.url) {
          await copyToClipboard(data.url)
        }
      }
    } else {
      // Fallback to clipboard
      if (data.url) {
        await copyToClipboard(data.url)
      }
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text)
        toast({
          title: "Copied to clipboard",
          description: "Link has been copied to your clipboard.",
        })
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        toast({
          title: "Copied to clipboard",
          description: "Link has been copied to your clipboard.",
        })
      }
    } catch (error) {
      toast({
        title: "Copy failed",
        description: "Unable to copy to clipboard.",
        variant: "destructive"
      })
    }
  }

  return {
    isInstallable,
    isInstalled,
    isOnline,
    installApp,
    shareContent,
    copyToClipboard
  }
}